import type { ValidationIssues } from '~/models/common'
import {
  buildRoutineAnchorRecord,
  type RoutineAnchorInput,
  type RoutineAnchorKind,
  type RoutineAnchorRecord,
  routineAnchorId,
} from '~/models/routineAnchor'
import type { IsoTimestamp } from '~/types/dates'
import { toIsoTimestamp } from '~/types/dates'
import type { Result } from '~/utils/result'
import { err, ok } from '~/utils/result'
import type { SolaDatabase } from '../db'
import { type StorageError, storageError, toStorageError } from '../errors'
import { notifyWritten } from '../invalidation'
import { STORE } from '../schema'

/**
 * Routine anchors (D-099).
 *
 * One record per kind, because the id is derived from the kind — see
 * `routineAnchorId`. An absent record is a kind the user has never configured,
 * which is an ordinary state and not a gap to be filled with a default time.
 *
 * **This repository holds no product rule.** Whether an anchor should prompt
 * depends on whether its task is outstanding, which means reading weigh-ins,
 * measurements, photos, reviews, sets and the backup timestamp — six stores this
 * one cannot see. That lives in `services/routineAnchorService.ts`, on the same
 * reasoning that keeps the daily check's correction boundary out of
 * `dailyCheckRepository`.
 */

export async function getRoutineAnchor(
  db: SolaDatabase,
  kind: RoutineAnchorKind,
): Promise<Result<RoutineAnchorRecord | undefined, StorageError>> {
  try {
    return ok(await db.get(STORE.routineAnchors, routineAnchorId(kind)))
  } catch (cause) {
    return err(toStorageError(cause))
  }
}

export async function listRoutineAnchors(
  db: SolaDatabase,
): Promise<Result<readonly RoutineAnchorRecord[], StorageError>> {
  try {
    return ok(await db.getAll(STORE.routineAnchors))
  } catch (cause) {
    return err(toStorageError(cause))
  }
}

/**
 * Every anchor that is switched on, read **through the `enabled` index**.
 *
 * Deliberately not a filter over `getAll`. Going through the index is what makes
 * the string-versus-boolean hazard observable: were `enabled` ever written as a
 * boolean, IndexedDB would silently leave the record out of the index rather
 * than reject it, this function would quietly return nothing, and every anchor
 * would stop prompting with no error anywhere. A test writes a boolean and
 * asserts exactly that.
 */
export async function listEnabledRoutineAnchors(
  db: SolaDatabase,
): Promise<Result<readonly RoutineAnchorRecord[], StorageError>> {
  try {
    return ok(await db.getAllFromIndex(STORE.routineAnchors, 'enabled', 'yes'))
  } catch (cause) {
    return err(toStorageError(cause))
  }
}

/**
 * Create or replace an anchor.
 *
 * A replace rather than a second record, which is what the derived id buys: two
 * weigh-in anchors cannot exist, so suppressing one cannot leave another
 * prompting.
 */
export async function putRoutineAnchor(
  db: SolaDatabase,
  input: RoutineAnchorInput,
  now: Date,
): Promise<Result<RoutineAnchorRecord, StorageError>> {
  try {
    const existing = await db.get(
      STORE.routineAnchors,
      routineAnchorId(input.kind as RoutineAnchorKind),
    )
    const built = buildRoutineAnchorRecord(input, toIsoTimestamp(now), existing)
    if (!built.ok) return err(invalid(built.error))

    await db.put(STORE.routineAnchors, built.value)
    notifyWritten(STORE.routineAnchors)
    return ok(built.value)
  } catch (cause) {
    return err(toStorageError(cause))
  }
}

/**
 * Replace an anchor's current suppression, or clear it.
 *
 * **One value in, one value out, and nothing accumulated.** Passing `undefined`
 * removes the key entirely rather than storing a null — there is no such thing
 * as an expired suppression worth keeping, and a cleared one that lingered would
 * be the first row of the history D-099 forbids.
 *
 * Suppressing an anchor that does not exist is a no-op rather than an error: the
 * user can only have pressed the button on a prompt, and a prompt for a
 * non-existent anchor is a bug elsewhere that this should not amplify into a
 * failed write.
 */
export async function setAnchorSuppression(
  db: SolaDatabase,
  kind: RoutineAnchorKind,
  until: IsoTimestamp | undefined,
  now: Date,
): Promise<Result<RoutineAnchorRecord | undefined, StorageError>> {
  try {
    const existing = await db.get(STORE.routineAnchors, routineAnchorId(kind))
    if (existing === undefined) return ok(undefined)

    const { suppressedUntil: _dropped, ...rest } = existing
    const next: RoutineAnchorRecord = {
      ...rest,
      ...(until === undefined ? {} : { suppressedUntil: until }),
      updatedAt: toIsoTimestamp(now),
    }

    await db.put(STORE.routineAnchors, next)
    notifyWritten(STORE.routineAnchors)
    return ok(next)
  } catch (cause) {
    return err(toStorageError(cause))
  }
}

function invalid(issues: ValidationIssues): StorageError {
  return storageError('invalidRecord', issues.map((entry) => entry.message).join(' '), issues)
}
